import React, { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom';
import { AcademyMyPageTabItems, AcademyMyPageLogoutButton } from "@/pages/academy/mypage/info";
import { Tabs, TabItem } from 'components/atoms/index';
import * as styles from 'components/organisms/Table/Table.css'
import Pagination from 'components/atoms/Pagination/Pagination';
import { Icon } from '@/icons';
import { Input } from 'components/atoms/Input/Input';
import { Button } from 'components/atoms/Button/Button';
import { DatePicker, ConfigProvider } from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/ko';
import locale from 'antd/locale/ko_KR';
import { academySubscriptionApi } from '@/services/api';
import { useSessionSlug } from '@/hooks/useSessionSlug';
import { getSlugPrefix } from '@/utils/slug';

interface PaymentHistory {
    id: string
    paymentStatus: string
    paymentType: string
    amount: number
    transactionDate: string | null
}

const formatTransactionDate = (value?: string | null) => {
    if (!value) return '-';
    const parsed = dayjs(value);
    if (parsed.isValid()) {
        return parsed.format('YYYY.MM.DD');
    }
    return value;
};

// 컬럼 렌더링용 메타 정보
const columns = [
    { key: 'id' as const, render: (row: PaymentHistory, index: number) => index + 1 },
    { key: 'paymentStatus' as const, render: (row: PaymentHistory) => renderPaymentStatus(row.paymentStatus) },
    { key: 'paymentType' as const, render: (row: PaymentHistory) => renderPaymentType(row.paymentType) },
    { key: 'amount' as const, render: (row: PaymentHistory) => (
        <div className="flex gap-2 items-center">
            <span className={row.amount < 0 ? 'text-red-500' : 'text-gray-900'}>
                {row.amount.toLocaleString()}원
            </span>
        </div>
    ) },
    { key: 'transactionDate' as const, render: (row: PaymentHistory) => (
        <div className="flex gap-2 items-center">
            <span>{formatTransactionDate(row.transactionDate)}</span>
        </div>
    ) },
]

function renderPaymentStatus(status: string) {
    switch (status) {
        case '부분환불':
        case 'partial_refund':
            return <span className="font-medium text-orange-600">부분환불</span>;
        case '취소':
            return <span className="font-medium text-red-600">취소</span>;
        case '실패':
            return <span className="font-medium text-red-500">결제실패</span>;
        case '대기':
            return <span className="font-medium text-yellow-600">대기</span>;
        default:
            return <span className="font-medium text-emerald-600">결제</span>;
    }
}

function renderPaymentType(type: string) {
    return <span className="font-medium">{type}</span>
}

export default function AcademyMypagePaymentHistory() {
    // dayjs 한국어 로케일 설정
    dayjs.locale('ko');
    
    const [activeTab, setActiveTab] = useState<'info' | 'plan' | 'paper' | 'payment' | 'class'>('payment');
    const [startDate, setStartDate] = useState<dayjs.Dayjs | null>(null);
    const [endDate, setEndDate] = useState<dayjs.Dayjs | null>(null);
    const [searchKeyword, setSearchKeyword] = useState('');
    const [data, setData] = useState<PaymentHistory[]>([]);
    const [loading, setLoading] = useState(false);
    const [pagination, setPagination] = useState({
        total: 0,
        page: 1,
        pageSize: 10,
        totalPages: 0
    });
    const navigate = useNavigate();
    const location = useLocation();

    const { slug: sessionSlug, academyCode: sessionAcademyCode, status: sessionStatus, role: sessionRole } = useSessionSlug();
    const academyCode = sessionAcademyCode || '';
    const slugPrefix = sessionSlug ? `/${sessionSlug}` : getSlugPrefix(location.pathname);
    const storedRole = typeof window !== 'undefined' ? (localStorage.getItem('academy_role') || '') : '';
    const isTeacher = (sessionRole || storedRole || '').toUpperCase() === 'TEACHER';
    useEffect(() => {
        if (isTeacher) {
            navigate(`${slugPrefix}/academy/mypage/info`, { replace: true });
        }
    }, [isTeacher, slugPrefix, navigate]);

    // 데이터 로드 함수
    const loadPaymentHistory = async (page = 1) => {
        console.log('[PaymentHistory] Loading - academyCode:', academyCode, 'page:', page);
        
        if (!academyCode || sessionStatus !== 'ready') {
            console.error('[PaymentHistory] academyCode is empty!');
            return;
        }
        
        setLoading(true);
        try {
            const res = await academySubscriptionApi.paymentHistory({
                academyCode,
                search: searchKeyword || undefined,
                startDate: startDate ? startDate.format('YYYY-MM-DD') : undefined,
                endDate: endDate ? endDate.format('YYYY-MM-DD') : undefined,
                page,
                pageSize: pagination.pageSize
            });
            
            console.log('[PaymentHistory] API Response:', res);
            
            const responseData = res?.data || res;
            
            console.log('[PaymentHistory] Parsed data:', responseData);
            console.log('[PaymentHistory] Items count:', responseData.items?.length);
            
            setData(responseData.items || []);
            setPagination({
                total: responseData.total || 0,
                page: responseData.page || 1,
                pageSize: responseData.pageSize || 10,
                totalPages: responseData.totalPages || 0
            });
        } catch (error) {
            console.error('[PaymentHistory] 결제 이력 조회 실패:', error);
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    // 초기 로드
    useEffect(() => {
        loadPaymentHistory(1);
    }, [academyCode, sessionStatus]);

    // 검색 핸들러
    const handleSearch = () => {
        loadPaymentHistory(1);
    };

    
    if (isTeacher) {
        return null;
    }

    return (
        <main className='flex flex-col flex-1'>
            <div className='flex flex-row justify-between items-center pt-8'>
                <h1 className='heading1'>마이페이지</h1>
                <AcademyMyPageLogoutButton />
            </div>
            <div className="flex flex-col flex-1 mt-5">
                <AcademyMyPageTabItems activeTab={activeTab} setActiveTab={setActiveTab} />
                <div className='flex flex-col flex-1'>
                    <section className='flex flex-col gap-4 my-5 w-full'>
                        <div className='flex gap-4 items-center'>
                            <ConfigProvider
                                locale={locale}
                                theme={{
                                    token: {
                                        colorPrimary: '#6366f1', // indigo-500
                                        borderRadius: 10,
                                        fontSize: 16,
                                        colorBorder: '#E5E7EB', // 기본 테두리 색상 (gray-300)
                                        colorBorderSecondary: '#E5E7EB', // 보조 테두리 색상 (gray-200)
                                    },
                                }}
                            >
                                <Input
                                    placeholder='검색어를 입력해주세요.'
                                    className='max-w-[300px]'
                                    icon={<Icon name="search" size={20} />}
                                    value={searchKeyword}
                                    onChange={(e) => setSearchKeyword(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSearch();
                                        }
                                    }}
                                />
                                <div className='flex gap-2 items-center'>
                                    <DatePicker
                                        placeholder="시작일"
                                        value={startDate}
                                        onChange={setStartDate}
                                        format="YYYY-MM-DD"
                                        style={{ width: 200, height: 44 }}
                                    />
                                    <span className='text-gray-500'>~</span>
                                    <DatePicker
                                        placeholder="종료일"
                                        value={endDate}
                                        onChange={setEndDate}
                                        format="YYYY-MM-DD"
                                        style={{ width: 200, height: 44 }}
                                    />
                                </div>
                            </ConfigProvider>
                            <Button onClick={handleSearch}>검색</Button>
                        </div>
                    </section>
                    
                    <section className='flex flex-col flex-1 justify-between w-full'>
            {loading ? (
                <div className='flex justify-center items-center py-20'>
                    <p className='text-gray-500'>로딩 중...</p>
                </div>
            ) : data.length === 0 ? (
                <div className='flex justify-center items-center py-20'>
                    <p className='text-gray-500'>결제 이력이 없습니다.</p>
                </div>
            ) : (
            <div className='w-full'>
                {/* 테이블 해더 pc 용*/}
                <ul className={`${styles.academyHeaderList} w-full`}>
                    <li className={`${styles.headerItem} max-w-[80px] justify-center`}>순번</li>
                    <li className={`${styles.headerItem}  max-w-[150px] `}>결제 구분</li>
                    <li className={`${styles.headerItem}  `}>결제 유형</li>
                    <li className={`${styles.headerItem} `}>결제 금액</li>
                    <li className={`${styles.headerItem} `}>거래일</li>
                </ul>
                <div className='flex flex-col w-full lg:gap-0'>
                    {data.map((row, index) => (
                        <ul 
                            key={row.id} 
                            className={`${styles.academyBodyList}   cursor-pointer hover:bg-gray-50 transition-colors  lg:!flex`}
                        >
                        {columns.map(({ key, render }) => {
                            const globalIndex = (pagination.page - 1) * pagination.pageSize + index;
                            const indexForRender = key === 'id' 
                                ? (pagination.total - globalIndex - 1) 
                                : globalIndex;
                            return (
                                <li
                                    key={key}
                                    className={`${styles.academyBodyItem} ${
                                        key === 'id' ? 'max-w-[80px] justify-center' :
                                        key === 'paymentStatus' ? 'max-w-[150px]' :
                                        key === 'paymentType' ? '' :
                                        key === 'amount' ? 'text-sm text-gray-500  lg:static col-span-1' :
                                        key === 'transactionDate' ? 'text-sm text-gray-500 ' :
                                        ''
                                    }`}
                                >
                                    {render(row, indexForRender)}
                                </li>
                            );
                        })}
                        </ul>
                    ))}
                </div>
            </div>
            )}
                <div className='py-5'>
            <Pagination
            total={pagination.total}
            pageSize={pagination.pageSize}
            current={pagination.page}
            onChange={(page) => loadPaymentHistory(page)}
            />
            </div>
                    </section>
                </div>
            </div>
                        
        </main>
    )
}